import React, { useEffect, useState } from 'react'

import { InnosQueryClientProvider, innosQueryClient } from '@inno-tools/query'
import { useSyncI18nLanguage } from '@inno-tools/react-hooks'
import { ToasterContainer } from '@inno-ui/toast'
import { QueryClientProvider } from '@tanstack/react-query'
import {
  LazyMotion,
  MotionConfig,
  Transition,
  domAnimation
} from 'motion/react'
import { I18nextProvider } from 'react-i18next'
import { BrowserRouter } from 'react-router-dom'
import { AnalyticsTracker } from 'shared/components/AnalyticsTracker/AnalyticsTracker'
import { useAnalyticsParentOwner } from 'shared/components/AnalyticsTracker/useAnalyticsParentOwner'
import { AppFocusBroadcaster } from 'shared/components/AppFocusBroadcaster/AppFocusBroadcaster'
import { FeatureFlagsLoader } from 'shared/components/FeatureFlagsLoader'
import { i18nextContainer } from 'shared/configs/i18n/config'
import { CustomEvents } from 'shared/constants/customEvents'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { UserContextProvider } from 'shared/contexts/User'
import {
  useAnalytics,
  useBroadcastUsercentricsConsent,
  useUsercentricsConsent
} from 'shared/hooks/analytics'
import { useThemeService } from 'shared/hooks/useThemeManager'
import { LoggerProvider } from 'shared/services/logger'
import { usePreferencesStore } from 'shared/stores/preferencesStore'
import { listenCustomEvent } from 'shared/utils/customEvents'
import { isProd } from 'shared/utils/isProd'
import { lsGetItem } from 'shared/utils/local-storage'

import './App.module.scss'
import { Routes } from './Routes'
import { startServiceWorkers } from './shared/services/registerServiceWorkers'
import './styles/global.scss'

const appleSpring: Transition = {
  type: 'spring',
  stiffness: 700,
  damping: 40,
  mass: 1
}

const App: React.FC = () => {
  const [user, setUser] = useState(() => lsGetItem(LocalStorageKey.USER))

  useSyncI18nLanguage(i18nextContainer)
  useThemeService()

  const analyticsConsent = useUsercentricsConsent()
  useBroadcastUsercentricsConsent(analyticsConsent)
  const isAnalyticsEnabled = isProd()
  const analyticsOwnedByParent = useAnalyticsParentOwner()

  useAnalytics(user, {
    amplitude: {
      enabled: isAnalyticsEnabled && analyticsConsent.amplitude,
      apiKey: process.env.REACT_APP_AMPLITUDE_API_KEY ?? ''
    },
    googleAnalytics: {
      enabled:
        isAnalyticsEnabled &&
        analyticsConsent.googleAnalytics &&
        analyticsOwnedByParent === false,
      id: process.env.REACT_APP_GA_ID ?? ''
    },
    clarity: {
      enabled: isAnalyticsEnabled && analyticsConsent.clarity,
      projectId: process.env.REACT_APP_CLARITY_ID ?? ''
    }
  })

  useEffect(() => {
    const { cachingEnabled, cachedAppIds } = usePreferencesStore.getState()
    if (!cachingEnabled) return
    startServiceWorkers(cachedAppIds)
  }, [])

  useEffect(() => {
    const unsubscribe = listenCustomEvent(
      CustomEvents.AUTH_USER_DATA_UPDATED,
      event => {
        setUser(event.user)
      }
    )

    return unsubscribe
  }, [])

  return (
    <QueryClientProvider client={innosQueryClient}>
      <InnosQueryClientProvider hasDevTools>
        <FeatureFlagsLoader user={user} />
        <UserContextProvider user={user}>
          <LazyMotion features={domAnimation}>
            <MotionConfig transition={appleSpring}>
              <I18nextProvider i18n={i18nextContainer}>
                <BrowserRouter
                  future={{
                    v7_relativeSplatPath: true,
                    v7_startTransition: true
                  }}
                >
                  <LoggerProvider
                    userId={user?.id}
                    organizationId={user?.organization_id}
                  >
                    <AnalyticsTracker
                      enabled={
                        isAnalyticsEnabled && analyticsConsent.googleAnalytics
                      }
                    />
                    <AppFocusBroadcaster />
                    <ToasterContainer />
                    <Routes />
                  </LoggerProvider>
                </BrowserRouter>
              </I18nextProvider>
            </MotionConfig>
          </LazyMotion>
        </UserContextProvider>
      </InnosQueryClientProvider>
    </QueryClientProvider>
  )
}

export { App }
